import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, getOrigin, setOrigin } from "@/lib/api";
import { Radar, LayoutDashboard, Boxes, Activity, GitMerge, SlidersHorizontal, LogOut, Database, UserCheck, TrendingUp, CalendarClock, Layers, FlaskConical, Network, Target, Scale, ShieldCheck } from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/research", label: "What to Check Next", icon: Target },
  { to: "/products", label: "Products", icon: Boxes },
  { to: "/trajectory", label: "Product Momentum", icon: TrendingUp },
  { to: "/graph", label: "Product Evidence", icon: Network },
  { to: "/capital-review", label: "Capital Review", icon: Scale },
  { to: "/grouping", label: "Fix Product Groups", icon: Layers },
  { to: "/nr-review", label: "Review New Products", icon: UserCheck },
  { to: "/snapshots", label: "Data Health", icon: CalendarClock },
  { to: "/pipeline", label: "Source Health", icon: Activity },
  { to: "/scoring", label: "Scoring Rules", icon: SlidersHorizontal },
  { to: "/experimental", label: "Early Product Signals", icon: FlaskConical },
  { to: "/system-health", label: "System Health", icon: ShieldCheck },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [mergePending, setMergePending] = useState(0);

  useEffect(() => {
    api.get("/amazon/radar-summary")
      .then((r) => setMergePending(r.data?.advanced_merge_review_pending || 0))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex bg-[#09090B] text-[#F4F4F5]">
      <aside className="w-60 shrink-0 border-r border-[#27272A] bg-[#0c0c0e] flex flex-col">
        <div className="px-4 py-5 border-b border-[#27272A] flex items-center gap-2">
          <Radar className="text-emerald-400" size={20} />
          <div>
            <div className="font-head font-bold tracking-tight leading-none">Breakout Radar</div>
            <div className="text-[10px] text-[#71717A] font-mono tracking-widest">PRODUCT INTEL ENGINE</div>
          </div>
        </div>
        <nav className="flex-1 py-3">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.replace(/\s+/g, "-").toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 text-sm border-l-2 transition-colors ${
                  isActive
                    ? "border-emerald-400 bg-[#18181B] text-[#F4F4F5]"
                    : "border-transparent text-[#A1A1AA] hover:bg-[#141416] hover:text-[#F4F4F5]"
                }`
              }
            >
              <n.icon size={16} />
              {n.label}
            </NavLink>
          ))}
          {mergePending > 0 && (
            <NavLink
              to="/entity"
              data-testid="nav-advanced-merge-review"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 text-sm border-l-2 transition-colors ${
                  isActive ? "border-emerald-400 bg-[#18181B] text-[#F4F4F5]"
                    : "border-transparent text-[#A1A1AA] hover:bg-[#141416] hover:text-[#F4F4F5]"
                }`
              }
            >
              <GitMerge size={16} /> Fix Product Matches
              <span className="ml-auto text-[10px] font-mono text-amber-400">{mergePending}</span>
            </NavLink>
          )}
        </nav>
        <div className="border-t border-[#27272A] p-4">
          <details className="mb-4">
            <summary className="text-[10px] uppercase tracking-widest text-[#52525B] font-mono mb-2 flex items-center gap-1 cursor-pointer list-none">
              <Database size={11} /> Demo data (legacy V1)
            </summary>
            <div className="flex border border-[#27272A] mt-2">
              {["live", "seed"].map((o) => (
                <button
                  key={o}
                  data-testid={`origin-toggle-${o}`}
                  onClick={() => {
                    setOrigin(o);
                    window.location.reload();
                  }}
                  className={`flex-1 px-2 py-1.5 text-[11px] font-mono uppercase tracking-widest transition-colors ${
                    getOrigin() === o ? "bg-emerald-500 text-[#09090B]" : "text-[#A1A1AA] hover:bg-[#141416]"
                  }`}
                >
                  {o}
                </button>
              ))}
            </div>
            <div className="text-[10px] text-[#52525B] font-mono mt-1">Only affects legacy scored pages.</div>
          </details>
          <div className="text-xs text-[#F4F4F5] font-mono truncate">{user?.email}</div>
          <div className="text-[10px] text-[#71717A] uppercase tracking-widest mb-3">{user?.role}</div>
          <button
            data-testid="logout-button"
            onClick={async () => {
              await logout();
              nav("/login");
            }}
            className="flex items-center gap-2 text-xs text-[#A1A1AA] hover:text-rose-400 transition-colors"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-x-hidden">
        <OriginBanner />
        {children}
      </main>
    </div>
  );
}

function OriginBanner() {
  const origin = getOrigin();
  if (origin !== "seed") return null;   // primary radar is always LIVE; only warn when viewing demo
  return (
    <div
      data-testid="origin-banner-seed"
      className="w-full px-6 py-2 text-xs font-mono flex items-center gap-2 border-b bg-amber-500/15 text-amber-300 border-amber-700/50"
    >
      <span className="w-2 h-2 rounded-full" style={{ background: "#fbbf24" }} />
      <strong className="tracking-widest">DEMO / SEED DATA</strong> — synthetic test dataset (legacy V1 scoring), not live radar intelligence
    </div>
  );
}
